import React from 'react';
import { Section } from '@/pages/LandingComplete';
import SectionHeader from './SectionHeader';
import { Button } from '@/components/ui/button';
import { CheckCircle, Shield, Laptop2, Users, Zap } from 'lucide-react';

interface PricePackage {
  name: string;
  description: string;
  price: string;
  priceUnit?: string;
  features: string[];
  recommended?: boolean;
  ctaText?: string;
}

interface PriceCategory {
  icon?: string;
  title: string;
  subtitle?: string;
  intro?: string;
  packages: PricePackage[];
}

interface PricesContent {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  categories?: PriceCategory[];
}

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Laptop2,
  Users,
  Shield,
  Zap,
};

export function DynamicPrices({ content }: { content: PricesContent }) {
  const categories = content.categories || [];

  return (
    <Section id="preise">
      <SectionHeader
        number="05"
        title={content.title || "Faire"}
        highlight={content.titleHighlight || "Preise"}
        subtitle={content.subtitle || "Einfach, verständlich und ohne Fachchinesisch."}
      />

      <div className="grid gap-10 lg:grid-cols-2 items-start">
        {categories.map((category, catIdx) => {
          const IconComponent = category.icon && iconMap[category.icon] ? iconMap[category.icon] : Laptop2;
          const iconColors = ['from-primary to-accent', 'from-emerald-500 to-emerald-600'];

          return (
            <div key={category.title + catIdx} className="bg-card rounded-[2rem] p-8 shadow-soft border border-navy/10 hover:shadow-lift transition-all duration-500">
              <div className="flex items-center gap-4 mb-6">
                <div className={`p-3.5 rounded-2xl bg-gradient-to-br ${iconColors[catIdx % iconColors.length]} shadow-lg`}>
                  <IconComponent size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-navy">{category.title}</h3>
                  {category.subtitle && <p className="text-muted-foreground">{category.subtitle}</p>}
                </div>
              </div>

              {category.intro && (
                <p className="text-muted-foreground mb-6 text-sm leading-relaxed bg-primary/5 p-4 rounded-2xl border border-primary/15">
                  {category.intro}
                </p>
              )}

              <div className="space-y-4">
                {category.packages.map((pkg, pkgIdx) => (
                  <div
                    key={pkg.name + pkgIdx}
                    className={`group relative rounded-2xl p-6 transition-all hover:shadow-lift ${
                      pkg.recommended
                        ? 'border-2 border-primary bg-primary/[0.04] shadow-soft'
                        : 'border border-navy/[0.12] bg-background hover:border-primary/50'
                    }`}
                  >
                    {pkg.recommended && (
                      <div className="absolute -top-3 left-6">
                        <span className="bg-gradient-to-r from-primary to-accent text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          ⭐ Empfohlen
                        </span>
                      </div>
                    )}

                    <div className={`flex flex-col md:flex-row md:items-start md:justify-between gap-4 ${pkg.recommended ? 'mt-2' : ''}`}>
                      <div className="flex-1">
                        <div className="font-bold text-navy text-lg mb-1">{pkg.name}</div>
                        <p className="text-sm text-muted-foreground mb-3 italic">{pkg.description}</p>
                        <ul className="space-y-1.5">
                          {pkg.features.map((feature, fIdx) => (
                            <li key={fIdx} className="flex items-start gap-2 text-muted-foreground text-sm">
                              <CheckCircle size={16} className="mt-0.5 text-emerald-500 shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="md:ml-6 flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start">
                        <div className="text-left md:text-right">
                          <div className="text-2xl font-extrabold text-navy leading-tight whitespace-nowrap">{pkg.price}</div>
                          {pkg.priceUnit && (
                            <div className="text-sm font-semibold text-muted-foreground">/ {pkg.priceUnit}</div>
                          )}
                        </div>
                        <Button
                          onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                          variant={pkg.recommended ? "default" : "outline"}
                          size="sm"
                          className={pkg.recommended
                            ? "text-sm rounded-full bg-primary hover:bg-primary/90 text-white"
                            : "text-sm rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                          }
                        >
                          {pkg.ctaText || 'Jetzt anfragen'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
